import React, { useState, useEffect, useRef } from "react";
import { supabase } from "../lib/supabase";
import { SUPABASE_URL, SUPABASE_ANON_KEY } from "../lib/supabaseConfig";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { gsap } from "gsap";
import {
  Lock,
  Eye,
  EyeOff,
  Shield,
  Loader2,
  CheckCircle,
  XCircle,
  AlertCircle,
} from "lucide-react";

const ResetPassword = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [tokenValid, setTokenValid] = useState(false);
  const [gridDimensions, setGridDimensions] = useState({ rows: 0, cols: 0 });
  const [debugInfo, setDebugInfo] = useState("");

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Refs for animations
  const containerRef = useRef(null);
  const leftPanelRef = useRef(null);
  const rightPanelRef = useRef(null);
  const formRef = useRef(null);

  // Set grid dimensions
  useEffect(() => {
    const updateGridDimensions = () => {
      setGridDimensions({
        rows: Math.ceil(window.innerHeight / 40),
        cols: Math.ceil(window.innerWidth / 40),
      });
    };

    updateGridDimensions();
    window.addEventListener("resize", updateGridDimensions);

    return () => window.removeEventListener("resize", updateGridDimensions);
  }, []);

  // Verify reset token on component mount
  useEffect(() => {
    const verifyToken = async () => {
      try {
        console.log("🔍 Starting token verification...");
        const token = searchParams.get("token");
        const allParams = Object.fromEntries(searchParams.entries());

        console.log("🔍 URL search params:", allParams);
        console.log("🔍 Token from URL:", token);

        setDebugInfo(`Token from URL: ${token ? "✅ Present" : "❌ Missing"}`);

        if (!token) {
          console.error("❌ No token provided in URL");
          setError("Invalid reset link. No token provided.");
          setVerifying(false);
          return;
        }

        console.log("🔄 Calling verify_password_reset_token function...");
        setDebugInfo(`Verifying token: ${token.substring(0, 10)}...`);

        const { data, error } = await supabase.rpc(
          "verify_password_reset_token",
          {
            reset_token: token,
          }
        );

        console.log("🔄 Token verification response:", { data, error });

        if (error) {
          console.error("❌ Token verification error:", error);
          setDebugInfo(`Verification error: ${error.message}`);

          // Check if it's a function not found error
          if (
            error.message?.includes("function") &&
            error.message?.includes("does not exist")
          ) {
            setError(
              "Password reset system is not properly configured. Please contact support."
            );
          } else {
            setError(
              "Failed to verify reset token. Please try requesting a new reset link."
            );
          }
          setVerifying(false);
          return;
        }

        console.log("🔍 Verification data:", data);

        if (data?.success) {
          console.log("✅ Token is valid");
          setTokenValid(true);
          setDebugInfo("Token verified successfully!");
        } else {
          console.log("❌ Token verification failed:", data?.error);
          setError(data?.error || "Invalid or expired reset token.");
          setDebugInfo(
            `Verification failed: ${data?.error || "Unknown error"}`
          );
        }
      } catch (err) {
        console.error("💥 Unexpected error during token verification:", err);
        setError("An unexpected error occurred. Please try again.");
        setDebugInfo(`Unexpected error: ${err.message}`);
      } finally {
        setVerifying(false);
        console.log("🏁 Token verification process completed");
      }
    };

    // Add a delay to ensure proper mounting
    const timer = setTimeout(verifyToken, 100);
    return () => clearTimeout(timer);
  }, [searchParams]);

  // GSAP animations
  useEffect(() => {
    if (
      !verifying &&
      containerRef.current &&
      leftPanelRef.current &&
      rightPanelRef.current
    ) {
      try {
        const tl = gsap.timeline();

        // Set initial states
        gsap.set([leftPanelRef.current, rightPanelRef.current], {
          opacity: 0,
          x: -50,
        });

        gsap.set(rightPanelRef.current, {
          x: 50,
        });

        // Animate in
        tl.to([leftPanelRef.current, rightPanelRef.current], {
          opacity: 1,
          x: 0,
          duration: 0.8,
          ease: "power3.out",
          stagger: 0.2,
        });
      } catch (animError) {
        console.warn("Animation error:", animError);
        // Ensure elements are visible even if animation fails
        if (leftPanelRef.current) leftPanelRef.current.style.opacity = "1";
        if (rightPanelRef.current) rightPanelRef.current.style.opacity = "1";
      }
    }
  }, [verifying]);

  // CRITICAL FIX: Ensure content is always visible
  useEffect(() => {
    // Safety net to ensure content is visible after a delay
    const timer = setTimeout(() => {
      if (leftPanelRef.current) {
        leftPanelRef.current.style.opacity = "1";
        leftPanelRef.current.style.transform = "translateX(0)";
      }
      if (rightPanelRef.current) {
        rightPanelRef.current.style.opacity = "1";
        rightPanelRef.current.style.transform = "translateX(0)";
      }
    }, 1000); // Fallback after 1 second

    return () => clearTimeout(timer);
  }, [verifying]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters long");
      return;
    }

    const token = searchParams.get("token");
    if (!token) {
      setError("Invalid reset token");
      return;
    }

    setLoading(true);
    try {
      console.log("🔄 Submitting password reset...");

      // Use our secure Edge Function to reset password with admin privileges
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/reset-password`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            reset_token: token,
            new_password: password,
          }),
        }
      );

      const data = await response.json();
      console.log("🔄 Password reset response:", {
        status: response.status,
        data,
      });

      if (!response.ok) {
        throw new Error(data.error || "Failed to reset password");
      }

      if (data.success) {
        console.log("✅ Password reset successful");
        setSuccess(
          "Password updated successfully! You can now log in with your new password."
        );
        setTimeout(() => navigate("/login"), 3000);
      } else {
        throw new Error(data.error || "Failed to reset password");
      }
    } catch (err) {
      console.error("💥 Password reset error:", err);
      setError(err.message || "Failed to update password");
    }
    setLoading(false);
  };

  return verifying ? (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black flex items-center justify-center">
      <div className="text-center text-white">
        <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-[#B8460E]" />
        <h2 className="text-xl font-semibold mb-2">Verifying Reset Token</h2>
        <p className="text-gray-300">
          Please wait while we verify your reset link...
        </p>
        {debugInfo && (
          <div className="mt-4 text-xs text-gray-400 bg-white/5 rounded-lg p-3 max-w-md mx-auto">
            {debugInfo}
          </div>
        )}
      </div>
    </div>
  ) : (
    <div
      ref={containerRef}
      className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black flex items-center justify-center relative overflow-hidden"
    >
      {/* Grid background with center glow effect */}
      <div className="absolute inset-0">
        {/* Base grid pattern */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
            `,
            backgroundSize: "40px 40px",
          }}
        />

        {/* Radial gradient mask to create center glow and edge fade effect */}
        <div
          className="absolute inset-0"
          style={{
            background: `
              radial-gradient(circle at 50% 50%, 
                rgba(184, 70, 14, 0.15) 0%, 
                rgba(184, 70, 14, 0.08) 40%, 
                rgba(184, 70, 14, 0.03) 70%,
                rgba(184, 70, 14, 0) 100%
              )
            `,
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.08) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.08) 1px, transparent 1px)
            `,
            backgroundSize: "40px 40px",
            maskImage: `
              radial-gradient(circle at 50% 50%, 
                rgba(255,255,255,1) 0%, 
                rgba(255,255,255,0.8) 30%, 
                rgba(255,255,255,0.3) 60%,
                rgba(255,255,255,0.1) 80%,
                rgba(255,255,255,0) 100%
              )
            `,
            WebkitMaskImage: `
              radial-gradient(circle at 50% 50%, 
                rgba(255,255,255,1) 0%, 
                rgba(255,255,255,0.8) 30%, 
                rgba(255,255,255,0.3) 60%,
                rgba(255,255,255,0.1) 80%,
                rgba(255,255,255,0) 100%
              )
            `,
          }}
        />

        {/* Interactive grid overlay for hover effects */}
        <div className="absolute inset-0 pointer-events-none">
          {Array.from({ length: gridDimensions.rows }).map((_, row) =>
            Array.from({ length: gridDimensions.cols }).map((_, col) => (
              <div
                key={`${row}-${col}`}
                className="absolute w-10 h-10 pointer-events-auto transition-all duration-300"
                style={{
                  left: `${col * 40}px`,
                  top: `${row * 40}px`,
                  animation: "none",
                }}
                onMouseEnter={(e) => {
                  gsap.fromTo(
                    e.target,
                    {
                      backgroundColor: "rgba(184, 70, 14, 0)",
                      boxShadow: "0 0 0px rgba(184, 70, 14, 0)",
                      scale: 1,
                    },
                    {
                      backgroundColor: "rgba(184, 70, 14, 0.3)",
                      boxShadow:
                        "0 0 25px rgba(184, 70, 14, 0.8), inset 0 0 15px rgba(184, 70, 14, 0.3)",
                      scale: 1.05,
                      duration: 0.2,
                      ease: "power2.out",
                    }
                  );

                  const adjacentSquares = document.querySelectorAll(
                    `[data-row="${row}"][data-col="${
                      col - 1
                    }"], [data-row="${row}"][data-col="${
                      col + 1
                    }"], [data-row="${
                      row - 1
                    }"][data-col="${col}"], [data-row="${
                      row + 1
                    }"][data-col="${col}"]`
                  );
                  adjacentSquares.forEach((square, index) => {
                    gsap.fromTo(
                      square,
                      {
                        backgroundColor: "rgba(184, 70, 14, 0)",
                        boxShadow: "0 0 0px rgba(184, 70, 14, 0)",
                      },
                      {
                        backgroundColor: "rgba(184, 70, 14, 0.1)",
                        boxShadow: "0 0 10px rgba(184, 70, 14, 0.4)",
                        duration: 0.15,
                        delay: index * 0.05,
                        ease: "power2.out",
                        onComplete: () => {
                          gsap.to(square, {
                            backgroundColor: "rgba(184, 70, 14, 0)",
                            boxShadow: "0 0 0px rgba(184, 70, 14, 0)",
                            duration: 0.3,
                            ease: "power2.out",
                          });
                        },
                      }
                    );
                  });
                }}
                onMouseLeave={(e) => {
                  gsap.to(e.target, {
                    backgroundColor: "rgba(184, 70, 14, 0)",
                    boxShadow: "0 0 0px rgba(184, 70, 14, 0)",
                    scale: 1,
                    duration: 0.4,
                    ease: "power2.out",
                  });
                }}
                data-row={row}
                data-col={col}
              />
            ))
          )}
        </div>
      </div>

      {/* Main Centered Container */}
      <div className="relative z-10 w-full max-w-5xl mx-auto p-4 sm:p-6 lg:p-8">
        <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl lg:rounded-3xl shadow-2xl overflow-hidden">
          <div className="flex flex-col lg:flex-row">
            {/* Left Panel - Reset Form */}
            <div
              ref={leftPanelRef}
              className="w-full lg:w-1/2 p-4 sm:p-6 lg:p-8 flex items-center justify-center order-2 lg:order-1"
              style={{ opacity: 1 }} // Changed from 0 to 1 to prevent white page
            >
              <div className="w-full max-w-sm">
                <div ref={formRef}>
                  {/* Logo */}
                  <div className="flex justify-center mb-4">
                    <div className="w-12 h-12 lg:w-14 lg:h-14 bg-gradient-to-br from-[#B8460E] via-[#8B3A0E] to-[#B8460E] rounded-2xl flex items-center justify-center shadow-lg">
                      <Shield className="w-6 h-6 lg:w-7 lg:h-7 text-white" />
                    </div>
                  </div>

                  {/* Title */}
                  <div className="text-center mb-6">
                    <h1 className="text-2xl lg:text-3xl font-bold text-white mb-1">
                      Reset Password
                    </h1>
                    <p className="text-sm lg:text-base text-gray-300">
                      Enter your new password below
                    </p>
                  </div>

                  {/* Reset Form */}
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {error && (
                      <div className="bg-red-900/20 border border-red-500/50 text-red-300 px-4 py-2.5 rounded-xl text-sm">
                        {error}
                      </div>
                    )}

                    {success && (
                      <div className="bg-green-900/20 border border-green-500/50 text-green-300 px-4 py-2.5 rounded-xl text-sm flex items-center">
                        <CheckCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                        {success}
                      </div>
                    )}

                    {debugInfo && process.env.NODE_ENV === "development" && (
                      <div className="bg-[#B8460E]/20 border border-[#B8460E]/50 text-orange-300 px-4 py-2.5 rounded-xl text-sm flex items-center">
                        <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                        Debug: {debugInfo}
                      </div>
                    )}

                    {/* New Password Input */}
                    <div>
                      <label
                        htmlFor="password"
                        className="block text-sm font-medium text-gray-300 mb-1.5"
                      >
                        New Password
                      </label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          id="password"
                          type={showPassword ? "text" : "password"}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="w-full bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl pl-10 pr-12 py-2.5 lg:py-3 text-white placeholder-gray-400 focus:outline-none focus:border-[#B8460E] focus:ring-2 focus:ring-[#B8460E]/20 transition-all duration-200"
                          placeholder="Enter new password"
                          required
                          minLength={6}
                          autoFocus
                          disabled={loading}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-200 transition-colors"
                        >
                          {showPassword ? (
                            <EyeOff className="w-4 h-4" />
                          ) : (
                            <Eye className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Confirm Password Input */}
                    <div>
                      <label
                        htmlFor="confirmPassword"
                        className="block text-sm font-medium text-gray-300 mb-1.5"
                      >
                        Confirm Password
                      </label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          id="confirmPassword"
                          type={showConfirmPassword ? "text" : "password"}
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          className="w-full bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl pl-10 pr-12 py-2.5 lg:py-3 text-white placeholder-gray-400 focus:outline-none focus:border-[#B8460E] focus:ring-2 focus:ring-[#B8460E]/20 transition-all duration-200"
                          placeholder="Confirm new password"
                          required
                          minLength={6}
                          disabled={loading}
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setShowConfirmPassword(!showConfirmPassword)
                          }
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-200 transition-colors"
                        >
                          {showConfirmPassword ? (
                            <EyeOff className="w-4 h-4" />
                          ) : (
                            <Eye className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-gradient-to-r from-[#B8460E] via-[#8B3A0E] to-[#B8460E] hover:from-[#D4541A] hover:via-[#B8460E] hover:to-[#D4541A] disabled:opacity-50 disabled:cursor-not-allowed text-white py-2.5 lg:py-3 px-6 rounded-xl transition-all duration-200 font-semibold flex items-center justify-center text-sm lg:text-base shadow-lg hover:shadow-xl"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Updating Password...
                        </>
                      ) : (
                        <>
                          <Lock className="w-4 h-4 mr-2" />
                          Update Password
                        </>
                      )}
                    </button>

                    {/* Back to Login */}
                    <div className="text-center">
                      <Link
                        to="/login"
                        className="text-sm text-gray-300 hover:text-[#B8460E] transition-colors"
                      >
                        Back to Login
                      </Link>
                    </div>
                  </form>
                </div>
              </div>
            </div>

            {/* Right Panel - Security Info */}
            <div
              ref={rightPanelRef}
              className="w-full lg:w-1/2 bg-gradient-to-br from-[#B8460E] via-[#8B3A0E] to-[#B8460E] p-4 sm:p-6 lg:p-8 flex items-center justify-center order-1 lg:order-2 relative"
              style={{ opacity: 1 }} // Changed from 0 to 1 to prevent white page
            >
              {/* Background decoration */}
              <div className="absolute inset-0 bg-black/10"></div>
              <div className="absolute -top-40 -left-40 w-80 h-80 bg-white/10 rounded-full blur-3xl"></div>
              <div className="absolute -bottom-40 -right-40 w-96 h-96 bg-white/5 rounded-full blur-3xl"></div>

              <div className="relative z-10 text-white text-center max-w-md">
                <div className="mb-6">
                  <div className="w-16 h-16 lg:w-20 lg:h-20 bg-white/20 rounded-3xl mx-auto mb-4 flex items-center justify-center shadow-2xl backdrop-blur-sm">
                    <Lock className="w-8 h-8 lg:w-10 lg:h-10 text-white" />
                  </div>
                  <h2 className="text-xl lg:text-2xl font-bold text-white mb-2">
                    Secure Password Reset
                  </h2>
                  <p className="text-sm lg:text-base text-orange-100 leading-relaxed">
                    Choose a strong password to keep your account secure.
                  </p>
                </div>

                {/* Security Tips */}
                <div className="text-left space-y-3">
                  <div className="flex items-start space-x-3 bg-white/10 backdrop-blur-sm rounded-xl p-3">
                    <CheckCircle className="w-5 h-5 text-orange-300 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-orange-100">
                      Use at least 8 characters
                    </p>
                  </div>
                  <div className="flex items-start space-x-3 bg-white/10 backdrop-blur-sm rounded-xl p-3">
                    <CheckCircle className="w-5 h-5 text-orange-300 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-orange-100">
                      Include uppercase and lowercase letters
                    </p>
                  </div>
                  <div className="flex items-start space-x-3 bg-white/10 backdrop-blur-sm rounded-xl p-3">
                    <CheckCircle className="w-5 h-5 text-orange-300 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-orange-100">
                      Add numbers and special characters
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
